import { z } from 'zod';
import { slugSchema } from './catalog.js';
import { MAX_DESIGN_PAGES } from './cart.js';

/**
 * Opsi, nilai opsi, harga per kombinasi, dan tier satu produk, disunting
 * sekaligus dari admin. Dikirim utuh dan ditulis dalam satu transaksi supaya
 * override tidak pernah menunjuk nilai opsi yang sudah dihapus.
 *
 * Key opsi dan value nilai opsi tidak bisa diganti setelah tersimpan:
 * keranjang dan pesanan menyimpan pilihan pelanggan dengan key/value itu.
 */

export const OPTION_INPUTS = ['PILL', 'SELECT', 'RANGE', 'TEXT'] as const;

const rupiah = z.number().int().min(0).max(1_000_000_000);

export const pricingOptionValueSchema = z.object({
  value: slugSchema,
  label: z.string().trim().min(1).max(120),
  helpText: z.string().trim().max(240).nullable().default(null),
  isDefault: z.boolean().default(false),
  priceDelta: z.number().int().min(-1_000_000_000).max(1_000_000_000).default(0),
  priceFactor: z.number().gt(0).max(1000).default(1),
  weightDeltaGram: z.number().int().min(-1_000_000).max(1_000_000).default(0),
  /** Ukuran jadi varian dalam mm; desimal satu angka (85,6). */
  widthMm: z.number().min(1).max(100_000).nullable().default(null),
  heightMm: z.number().min(1).max(100_000).nullable().default(null),
  /** Jumlah sisi desain varian ini (KEPUTUSAN 4.6); null = tidak menentukan. */
  sides: z.number().int().min(1).max(MAX_DESIGN_PAGES).nullable().default(null),
});

export const pricingOptionSchema = z.object({
  key: slugSchema,
  label: z.string().trim().min(1).max(120),
  helpText: z.string().trim().max(240).nullable().default(null),
  input: z.enum(OPTION_INPUTS).default('PILL'),
  required: z.boolean().default(true),
  values: z.array(pricingOptionValueSchema).min(1).max(200),
});

export const pricingOverrideSchema = z.object({
  selections: z.record(z.string(), z.string()),
  unitPrice: rupiah,
  /** Diabaikan mesin harga bila tidak lebih tinggi dari unitPrice (data impor CI banyak begitu). */
  compareAtPrice: rupiah.nullable().default(null),
});

export const pricingTierSchema = z.object({
  minQty: z.number().int().min(1).max(1_000_000),
  /** 0.88 = hemat 12%. Di atas 1 tidak masuk akal untuk potongan jumlah. */
  factor: z.number().gt(0).max(1),
  label: z.string().trim().max(60).nullable().default(null),
});

/** Bentuk normal kombinasi: pasangan key=value diurut lalu disambung '|'. */
export function overrideMatchKey(selections: Record<string, string>): string {
  return Object.entries(selections)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join('|');
}

export const productPricingSchema = z
  .object({
    options: z.array(pricingOptionSchema).max(10),
    overrides: z.array(pricingOverrideSchema).max(5000),
    tiers: z.array(pricingTierSchema).max(20),
  })
  .superRefine((data, ctx) => {
    const issue = (path: (string | number)[], message: string) =>
      ctx.addIssue({ code: 'custom', path, message });

    const valuesByKey = new Map<string, Set<string>>();
    data.options.forEach((option, i) => {
      if (valuesByKey.has(option.key)) {
        issue(['options', i, 'key'], `Key opsi "${option.key}" dipakai dua kali`);
      }
      const seen = new Set<string>();
      option.values.forEach((val, j) => {
        if (seen.has(val.value)) {
          issue(['options', i, 'values', j, 'value'], `Nilai "${val.value}" dipakai dua kali`);
        }
        seen.add(val.value);
      });
      if (option.values.filter((val) => val.isDefault).length > 1) {
        issue(['options', i, 'values'], `Opsi "${option.label}" punya lebih dari satu default`);
      }
      valuesByKey.set(option.key, seen);
    });

    const matchKeys = new Set<string>();
    data.overrides.forEach((ov, i) => {
      const entries = Object.entries(ov.selections);
      if (entries.length === 0) {
        issue(['overrides', i], 'Harga kombinasi harus menyebut minimal satu opsi');
      }
      for (const [key, value] of entries) {
        const allowed = valuesByKey.get(key);
        if (!allowed) issue(['overrides', i], `Opsi "${key}" tidak ada`);
        else if (!allowed.has(value))
          issue(['overrides', i], `Nilai "${value}" tidak ada di "${key}"`);
      }
      const mk = overrideMatchKey(ov.selections);
      if (matchKeys.has(mk)) issue(['overrides', i], 'Kombinasi ini sudah punya harga');
      matchKeys.add(mk);
    });

    const minQtys = new Set<number>();
    data.tiers.forEach((tier, i) => {
      if (minQtys.has(tier.minQty)) {
        issue(['tiers', i, 'minQty'], `Tier ${tier.minQty}+ dipakai dua kali`);
      }
      minQtys.add(tier.minQty);
    });
  });

export type PricingOptionValue = z.infer<typeof pricingOptionValueSchema>;
export type PricingOption = z.infer<typeof pricingOptionSchema>;
export type PricingOverride = z.infer<typeof pricingOverrideSchema>;
export type PricingTier = z.infer<typeof pricingTierSchema>;
export type ProductPricingInput = z.infer<typeof productPricingSchema>;
